import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'gatsby'
import { Location } from '@reach/router'
import PropTypes from 'prop-types'
import FsLightbox from 'fslightbox-react'
import './header.scss'

const NAV_ICON_PATH = '/app-icons/nav/'

const SOLUTIONS = [
    { title: 'Onboarding & Compliance', description: 'Get new hires trained and signed off fast', to: '/features/#onboarding-compliance', icon: 'onboarding_compliancesvg.svg' },
    { title: 'Form Tools & Ongoing Tasks', description: 'Report in seconds, follow up in one place', to: '/features/#form-tools-ongoing-tasks', icon: 'forms_tools.svg' },
    { title: 'Communication & Collaboration', description: 'Share updates and hear from your crew', to: '/features/#communication-collaboration', icon: 'communication_collaboration.svg' },
    { title: 'Culture & Recognition', description: 'Celebrate milestones, reward great work', to: '/features/#culture-recognition', icon: 'culture_recognition.svg' },
    { title: 'References', description: 'Everything handy when the auditor arrives', to: '/features/#references', icon: 'references.svg' },
]

const RESOURCES = [
    { title: 'Help Center', description: 'Guides for every part of Workhub', href: 'http://help.workhub.com/en/', icon: 'quiz.svg' },
    { title: 'Release Notes', description: "What's new and what we've fixed", to: '/about/news/', icon: 'components.svg' },
    { title: 'Product Roadmap', description: "What we're building next", href: 'https://portal.productboard.com/workhub/', icon: 'strategy.svg' },
    { title: 'WorkhubU', description: 'Short videos, no fluff', href: 'https://www.youtube.com/channel/UC7jQkB2O_sVOUsylseAp9RA', icon: 'animated_images.svg' },
    { title: 'Partners', description: 'Implementation and compliance guidance', to: '/resources/partners/', icon: 'handshake.svg' },
]

const MENUS = [
    { id: 'why-workhub', label: 'Why Workhub', to: '/about/what-is-workhub/' },
    { id: 'solutions', label: 'Solutions', items: SOLUTIONS, overview: { label: 'See Solutions Overview', to: '/features/' } },
    { id: 'pricing', label: 'Pricing', to: '/pricing/' },
    { id: 'resources', label: 'Resources', items: RESOURCES },
]

const LOGIN_URL = 'https://app.workhub.com'
const DESKTOP_QUERY = '(min-width: 1171px)'

const ChevronIcon = ({ className }) => (
    <svg className={className} width="24" height="24" viewBox="-6 -8.5 24 24" fill="none" aria-hidden="true">
        <path d="M5.57499 4.175L9.47499 0.275C9.65833 0.0916666 9.89166 0 10.175 0C10.4583 0 10.6917 0.0916666 10.875 0.275C11.0583 0.458333 11.15 0.691667 11.15 0.975C11.15 1.25833 11.0583 1.49167 10.875 1.675L6.27499 6.275C6.17499 6.375 6.06666 6.44583 5.94999 6.4875C5.83333 6.52917 5.70833 6.55 5.57499 6.55C5.44166 6.55 5.31666 6.52917 5.19999 6.4875C5.08333 6.44583 4.97499 6.375 4.87499 6.275L0.274994 1.675C0.0916604 1.49167 0 1.25833 0 0.975C0 0.691666 0.0916605 0.458333 0.274994 0.275C0.458327 0.0916662 0.69166 0 0.974994 0C1.25833 0 1.49166 0.0916662 1.67499 0.275L5.57499 4.175Z" fill="currentColor" />
    </svg>
)

const ExternalIcon = () => (
    <img src={`${NAV_ICON_PATH}external_link.svg`} alt="" className="nav-dropdown-item-external-icon" />
)

const ArrowIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const MenuIcon = ({ isOpen }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {isOpen ? (
            <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        ) : (
            <path d="M4 7H20M4 12H20M4 17H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        )}
    </svg>
)

const DropdownItem = ({ item, onSelect, selected = false }) => {
    const className = `nav-dropdown-item-link${selected ? ' nav-dropdown-item-link-selected' : ''}`
    const content = (
        <>
            <span className="nav-dropdown-item-icon-container">
                <img src={`${NAV_ICON_PATH}${item.icon}`} alt="" className="nav-dropdown-item-icon" />
            </span>
            <span className="nav-dropdown-item-text-container">
                <span className="nav-dropdown-item-title-text">
                    {item.title}
                    {item.href && <ExternalIcon />}
                </span>
                <span className="nav-dropdown-item-body-text">{item.description}</span>
            </span>
        </>
    )

    return item.href ? (
        <a href={item.href} target="_blank" rel="noopener noreferrer" className={className} onClick={onSelect}>
            {content}
        </a>
    ) : (
        <Link to={item.to} className={className} onClick={onSelect} aria-current={selected ? 'location' : undefined}>
            {content}
        </Link>
    )
}

const Header = ({ location, isModalOpen }) => {
    const headerRef = useRef(null)
    const [isScrolled, setIsScrolled] = useState(false)
    const [openDesktopMenu, setOpenDesktopMenu] = useState(null)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [openMobileSection, setOpenMobileSection] = useState(null)
    const [isDemoOpen, setIsDemoOpen] = useState(false)

    const closeMenus = () => {
        setOpenDesktopMenu(null)
        setIsMobileMenuOpen(false)
        setOpenMobileSection(null)
    }

    // Shadow only once the page has scrolled
    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 0)
        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    // Close everything when the page changes
    useEffect(() => {
        closeMenus()
    }, [location.pathname])

    // Close on Escape or a click outside the header
    useEffect(() => {
        const handleKeyDown = event => event.key === 'Escape' && closeMenus()
        const handlePointerDown = event => {
            if (headerRef.current && !headerRef.current.contains(event.target)) setOpenDesktopMenu(null)
        }
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handlePointerDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('mousedown', handlePointerDown)
        }
    }, [])

    // Close the mobile menu if the window is widened to desktop
    useEffect(() => {
        const desktop = window.matchMedia(DESKTOP_QUERY)
        const handleChange = event => event.matches && setIsMobileMenuOpen(false)
        desktop.addListener(handleChange)
        return () => desktop.removeListener(handleChange)
    }, [])

    // Stop the page behind the open mobile menu from scrolling
    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : ''
        return () => { document.body.style.overflow = '' }
    }, [isMobileMenuOpen])

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(isOpen => !isOpen)
        setOpenMobileSection(null)
    }

    const openDemo = () => {
        closeMenus()
        setIsDemoOpen(isOpen => !isOpen)
    }

    const goToOverview = to => () => {
        closeMenus()
        if (location.pathname === to) window.scrollTo(0, 0)
    }

    const hoverOpen = id => event => event.pointerType === 'mouse' && setOpenDesktopMenu(id)
    // const hoverClose = event => event.pointerType === 'mouse' && setOpenDesktopMenu(null)
    const hoverClose = () => {}
    const isSelectedSolution = item => {
        const hash = item.to.split('#')[1]
        return location.pathname === '/features/' && hash && location.hash === `#${hash}`
    }
    const isSelectedResource = item => {
        if (!item.to) return false
        const itemPath = item.to.replace(/\/$/, '')
        const currentPath = location.pathname.replace(/\/$/, '')
        return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`)
    }

    const demoFrame = (
        <div className="modal-contain">
            <iframe
                className="icontain-modal"
                src="https://meetings.hubspot.com/leo-skinner/worhub-discussion"
                width="90%"
                height="90%"
                scrolling="auto"
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
                style={{ overflow: 'auto' }}
                title="Book a demo"
            />
        </div>
    )

    const headerClassName = [
        'header_area header_area_six nav-header',
        isScrolled && 'nav-header-scrolled',
        isMobileMenuOpen && 'nav-header-open',
        isModalOpen === true && 'd-none',
    ].filter(Boolean).join(' ')

    return (
        <header ref={headerRef} className={headerClassName}>
            <div className="nav-content-container">
                <div className="nav-bar-container">
                    <div className="nav-main-container">
                        <Link className="nav-logo-link" to="/" onClick={closeMenus}>
                            <img className="nav-logo" src="/logo.png" srcSet="/logo2x.png 2x" alt="Workhub" />
                        </Link>

                        {/* Desktop menu */}
                        <nav className="nav-menu-container" aria-label="Main">
                            <ul className="nav-menu-list">
                                {MENUS.map(menu => {
                                    if (!menu.items) {
                                        return (
                                            <li key={menu.id} className="nav-menu-item">
                                                <Link to={menu.to} className="nav-menu-link">{menu.label}</Link>
                                            </li>
                                        )
                                    }

                                    const isOpen = openDesktopMenu === menu.id
                                    const panelId = `nav-dropdown-${menu.id}`

                                    return (
                                        <li
                                            key={menu.id}
                                            className="nav-menu-item"
                                            onPointerEnter={hoverOpen(menu.id)}
                                            onPointerLeave={hoverClose}
                                        >
                                            <button
                                                type="button"
                                                className={`nav-menu-link nav-menu-trigger${isOpen ? ' nav-menu-trigger-open' : ''}`}
                                                aria-expanded={isOpen}
                                                aria-controls={panelId}
                                                onClick={() => setOpenDesktopMenu(isOpen ? null : menu.id)}
                                            >
                                                {menu.label}
                                                <ChevronIcon className="nav-menu-chevron" />
                                            </button>
                                            {isOpen && (
                                                <div id={panelId} className="nav-dropdown-container">
                                                    <div className="nav-dropdown-panel-container">
                                                        <ul className="nav-dropdown-list">
                                                            {menu.items.map(item => (
                                                                <li key={item.title}>
                                                                    <DropdownItem
                                                                        item={item}
                                                                        onSelect={closeMenus}
                                                                        selected={
                                                                            (menu.id === 'solutions' && isSelectedSolution(item)) ||
                                                                            (menu.id === 'resources' && isSelectedResource(item))
                                                                        }
                                                                    />
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        {menu.overview && (
                                                            <Link
                                                                to={menu.overview.to}
                                                                className="nav-dropdown-overview-link"
                                                                onClick={goToOverview(menu.overview.to)}
                                                            >
                                                                {menu.overview.label}
                                                                <ArrowIcon />
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    )
                                })}
                            </ul>
                        </nav>
                    </div>

                    <div className="nav-action-container">
                        <button
                            type="button"
                            className="nav-demo-button"
                            onClick={openDemo}
                            data-track="cta"
                            data-cta-id="site_header_book_demo"
                            data-cta-location="header"
                            data-cta-destination="hubspot_meeting"
                        >
                            Book A Demo
                        </button>
                        <span className="nav-action-divider" aria-hidden="true" />
                        <div className="nav-auth-container">
                            <a
                                href={LOGIN_URL}
                                className="nav-login-button"
                                data-track="cta"
                                data-cta-id="site_header_login"
                                data-cta-location="header"
                                data-cta-destination="app_login"
                            >
                                Log In
                            </a>
                            <Link
                                to="/signup/"
                                className="nav-signup-button"
                                data-track="cta"
                                data-cta-id="site_header_sign_up"
                                data-cta-location="header"
                                data-cta-destination="signup"
                            >
                                Sign Up
                            </Link>
                        </div>
                        <span className="nav-toggle-divider" aria-hidden="true" />
                        <button
                            type="button"
                            className="nav-toggle-button"
                            aria-expanded={isMobileMenuOpen}
                            aria-controls="nav-mobile-menu"
                            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                            onClick={toggleMobileMenu}
                        >
                            <MenuIcon isOpen={isMobileMenuOpen} />
                        </button>
                    </div>
                </div>

                {/* Tablet and mobile menu */}
                {isMobileMenuOpen && (
                    <div id="nav-mobile-menu" className="nav-mobile-container">
                        <ul className="nav-mobile-list">
                            {MENUS.map(menu => {
                                if (!menu.items) {
                                    return (
                                        <li key={menu.id} className="nav-mobile-item">
                                            <Link to={menu.to} className="nav-mobile-link" onClick={closeMenus}>{menu.label}</Link>
                                        </li>
                                    )
                                }

                                const isOpen = openMobileSection === menu.id
                                const sectionId = `nav-mobile-${menu.id}`

                                return (
                                    <li key={menu.id} className="nav-mobile-item">
                                        <button
                                            type="button"
                                            className={`nav-mobile-link nav-mobile-trigger${isOpen ? ' nav-mobile-trigger-open' : ''}`}
                                            aria-expanded={isOpen}
                                            aria-controls={sectionId}
                                            onClick={() => setOpenMobileSection(isOpen ? null : menu.id)}
                                        >
                                            {menu.label}
                                            <ChevronIcon className="nav-menu-chevron" />
                                        </button>
                                        {isOpen && (
                                            <ul id={sectionId} className="nav-mobile-dropdown-list">
                                                {menu.items.map(item => (
                                                    <li key={item.title}>
                                                        <DropdownItem
                                                            item={item}
                                                            onSelect={closeMenus}
                                                            selected={
                                                                (menu.id === 'solutions' && isSelectedSolution(item)) ||
                                                                (menu.id === 'resources' && isSelectedResource(item))
                                                            }
                                                        />
                                                    </li>
                                                ))}
                                                {menu.overview && (
                                                    <li>
                                                        <Link
                                                            to={menu.overview.to}
                                                            className="nav-dropdown-overview-link"
                                                            onClick={goToOverview(menu.overview.to)}
                                                        >
                                                            {menu.overview.label}
                                                            <ArrowIcon />
                                                        </Link>
                                                    </li>
                                                )}
                                            </ul>
                                        )}
                                    </li>
                                )
                            })}
                        </ul>

                        <div className="nav-mobile-footer-container">
                            <button
                                type="button"
                                className="nav-demo-button"
                                onClick={openDemo}
                                data-track="cta"
                                data-cta-id="site_header_book_demo"
                                data-cta-location="header"
                                data-cta-destination="hubspot_meeting"
                            >
                                Book a Demo
                            </button>
                            <a
                                href={LOGIN_URL}
                                className="nav-login-button"
                                data-track="cta"
                                data-cta-id="site_header_login"
                                data-cta-location="header"
                                data-cta-destination="app_login"
                            >
                                Log In
                            </a>
                            <Link
                                to="/signup/"
                                className="nav-signup-button"
                                onClick={closeMenus}
                                data-track="cta"
                                data-cta-id="site_header_sign_up"
                                data-cta-location="header"
                                data-cta-destination="signup"
                            >
                                Sign Up
                            </Link>
                        </div>
                    </div>
                )}
            </div>

            <FsLightbox toggler={isDemoOpen} sources={[demoFrame]} />
        </header>
    )
}

Header.propTypes = {
    location: PropTypes.object.isRequired,
    isModalOpen: PropTypes.bool,
}

export default props => (
    <Location>
        {locationProps => <Header {...locationProps} {...props} />}
    </Location>
)